import { FC } from "react";

import { formatMoneyCents } from "shared/lib/helpers/numbers";
import {
  BONUS_2_QUALIFICATION_CRITERIA_TYPE,
  BONUS_QUALIFICATION_CRITERIA_TYPE,
} from "shared/lib/constants/qualification-criteria";
import {
  getBonusCriteriaLabel,
  getCampaignTiers,
} from "shared/lib/helpers/qualification-criteria";
import {
  BonusQualificationCriteriaTypeI,
  CampaignTiersI,
} from "shared/lib/interfaces/qualification-criteria";

import {
  QualificationCriteriaTable,
  QualificationCriteriaTablePropsI,
} from "./shared/base";

interface QualificationCriteriaTableWithPayoutExtensionPropsI extends QualificationCriteriaTablePropsI {
  payout: number;
  bonusPayout?: number;
  bonus2Payout?: number;
  /* Which bonus tiers to render. Defaults to inferring from the criteria text
   * so existing callers that only pass bonusPayout keep working.
   */
  tiers?: CampaignTiersI;
}

const PAYOUT_AMOUNT_FORMAT_CONFIG = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
};

/**
 *
 * @param payout - Base payout in cents
 * @param bonusPayout - Bonus payout in cents
 * @param texts - Map of QC values (bonus is optional) entered by the customer
 * during the onboarding
 * @param restTableProps - Basic props for the QualificationCriteriaTable component
 */
export const QualificationCriteriaTableWithPayout: FC<
  QualificationCriteriaTableWithPayoutExtensionPropsI
> = ({
  payout,
  bonusPayout,
  bonus2Payout,
  tiers: _tiers,
  texts,
  ...restTableProps
}) => {
  const tiers = _tiers ?? getCampaignTiers(texts);

  const bonusPayoutByType: Record<BonusQualificationCriteriaTypeI, number> = {
    [BONUS_QUALIFICATION_CRITERIA_TYPE]: bonusPayout ?? 0,
    [BONUS_2_QUALIFICATION_CRITERIA_TYPE]: bonus2Payout ?? 0,
  };

  // Only price a bonus row we actually have an amount for. A campaign can have
  // criterion text with no payout figure loaded yet, and a "$0 Caller Bonus"
  // row reads as broken.
  const bonusRows = tiers.bonusTypes.filter(
    (type) => bonusPayoutByType[type] > 0
  );

  return (
    <QualificationCriteriaTable
      {...restTableProps}
      texts={texts}
      rightExtension={{
        head: {
          content: "Payout",
          className: "text-right",
        },
        cells: [
          {
            className: "text-right",
            content: formatMoneyCents(payout, PAYOUT_AMOUNT_FORMAT_CONFIG),
            colSpan: 4,
          },
        ],
      }}
      bottomExtension={{
        rows: bonusRows.map((type) => ({
          type,
          cells: [
            { content: getBonusCriteriaLabel(type, tiers) },
            { content: texts[type] },
            {
              className: "text-right",
              content: `${formatMoneyCents(
                bonusPayoutByType[type],
                PAYOUT_AMOUNT_FORMAT_CONFIG
              )} Caller Bonus`,
            },
          ],
        })),
      }}
    />
  );
};
